import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getMatchHistoryDetail, getMyMatchHistory, getSquadMatchHistory } from '../lib/matchHistory';
import type { MatchHistoryPage } from '../types';
import { useAuth } from './useAuth';

export const matchHistoryKeys = {
  all: ['match-history'] as const,
  mine: () => [...matchHistoryKeys.all, 'mine'] as const,
  squad: (squadId: number | string | null) => [...matchHistoryKeys.all, 'squad', squadId] as const,
  detail: (matchId: string | undefined) => [...matchHistoryKeys.all, 'detail', matchId] as const,
};

export function useMyMatchHistory(enabled = true) {
  const { user } = useAuth();
  return useInfiniteQuery<MatchHistoryPage>({
    queryKey: [...matchHistoryKeys.mine(), user?.id],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      getMyMatchHistory({ cursor: typeof pageParam === 'string' ? pageParam : null, signal, expectedUserId: user?.id }),
    getNextPageParam: (page) => (page.hasMore ? page.nextCursor || undefined : undefined),
    staleTime: 30 * 1000,
    retry: false,
    enabled: enabled && Boolean(user),
  });
}

export function useSquadMatchHistory(squadId: number | string | null, enabled = true) {
  const { user } = useAuth();
  return useInfiniteQuery<MatchHistoryPage>({
    queryKey: [...matchHistoryKeys.squad(squadId), user?.id],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      getSquadMatchHistory(squadId as number | string, {
        cursor: typeof pageParam === 'string' ? pageParam : null,
        signal,
        expectedUserId: user?.id,
      }),
    getNextPageParam: (page) => (page.hasMore ? page.nextCursor || undefined : undefined),
    staleTime: 30 * 1000,
    retry: false,
    enabled: enabled && Boolean(user) && squadId != null,
  });
}

export function useMatchHistoryDetail(matchId: string | undefined, enabled = true) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...matchHistoryKeys.detail(matchId), user?.id],
    queryFn: ({ signal }) => getMatchHistoryDetail(matchId as string, signal, user?.id),
    staleTime: 30 * 1000,
    retry: false,
    enabled: enabled && Boolean(user) && Boolean(matchId),
  });
}
